import { Flex, Text } from "@mantine/core";

import { CompositeChart } from "@mantine/charts";
import { useResizeObserver } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Scatter } from "recharts";
import { chordDistanceToDegrees, isBlinkSentinel } from "./charts/gazeAngle";
import {
  formatTickAtInterval,
  getFirstPositiveTimestamp,
  getRoundedAxisMaximum,
  getTicksBetween,
} from "./charts/vfaChartAxes";

const SAFE_DISTANCE = 0.15;
const BLINK_ROW_HEIGHT = 2;
const Y_TICK_INTERVAL = 5;

export default function GazeTrackMap(props) {
  const { t } = useTranslation();
  const [ref] = useResizeObserver();
  const height = props.height ?? 150;

  const centerThreshold = 0;
  const acceptableMaxThreshold = chordDistanceToDegrees(SAFE_DISTANCE);
  const blinkRowValue = -BLINK_ROW_HEIGHT / 2;
  const points = props.data;

  if (!points || points.length === 0) {
    return null;
  }

  const isArrayOfObjects = typeof points[0] === "object" && !Array.isArray(points[0]);

  const firstTimeStamp = isArrayOfObjects
    ? getFirstPositiveTimestamp(points.map(({ Timestamp }) => Number(Timestamp)))
    : Number(points[0][0]);

  const rawPoints = points
    .map((p) => ({
      time: isArrayOfObjects
        ? Math.max(0, Number(p.Timestamp) - firstTimeStamp)
        : (Number(p[0]) - Number(firstTimeStamp)) / 1000,
      rawValue: isArrayOfObjects ? Number(p.Distance) : Number(p[1]),
    }))
    .filter(({ time, rawValue }) => Number.isFinite(time) && Number.isFinite(rawValue));

  if (rawPoints.length === 0) return null;

  const chartData = rawPoints.map(({ time, rawValue }) => ({
    time,
    value: isBlinkSentinel(rawValue) ? null : chordDistanceToDegrees(rawValue),
  }));

  const blinkPoints = rawPoints
    .filter(({ rawValue }) => isBlinkSentinel(rawValue))
    .map(({ time }) => ({ time, blink: blinkRowValue }));

  const maximumTime = Math.max(0, ...chartData.map(({ time }) => time));
  const xDomainMaximum = getRoundedAxisMaximum(maximumTime, 10);
  const xTicks = getTicksBetween(0, xDomainMaximum, 10);

  const maximumValue = Math.max(
    acceptableMaxThreshold,
    ...chartData.map(({ value }) => value).filter(Number.isFinite),
  );
  const yDomainMaximum = getRoundedAxisMaximum(maximumValue, Y_TICK_INTERVAL);
  const yTicks = getTicksBetween(0, yDomainMaximum, Y_TICK_INTERVAL);
  const yDomain = [-BLINK_ROW_HEIGHT, yDomainMaximum];

  return (
    <Flex ref={ref} direction="column">
      <Text fw={700} c="black" ta="center">
        {t("gaze")}
      </Text>

      <CompositeChart
        h={height}
        data={chartData}
        dataKey="time"
        series={[{ name: "value", color: "black", type: "line" }]}
        withLegend={false}
        withTooltip={false}
        textColor="black"
        gridColor="black"
        gridAxis="none"
        connectNulls={false}
        lineProps={{
          strokeWidth: 1,
          stroke: "black",
          dot: { r: 1.5, fill: "black", stroke: "black", strokeWidth: 1 },
          activeDot: { r: 3, stroke: "black", strokeWidth: 1 },
        }}
        referenceLines={[
          {
            y: blinkRowValue,
            color: "black",
            opacity: 0.5,
            strokeWidth: 0.1,
            label: t("blink"),
          },
          {
            y: centerThreshold,
            color: "black",
            opacity: 1,
            strokeWidth: 0.5,
            strokeDasharray: "5 5",
            label: t("center"),
            labelPosition: "right",
          },
          {
            y: acceptableMaxThreshold,
            color: "black",
            opacity: 0.5,
            strokeWidth: 1,
            strokeDasharray: "5 5",
            label: t("acceptable_maximum"),
          },
        ]}
        yAxisProps={{
          domain: yDomain,
          ticks: yTicks,
          tickFormatter: (value) => `${value.toFixed(1)}°`,
          width: 60,
          axisLine: { stroke: "black" },
          tickLine: { stroke: "black" },
          label: {
            value: t("gazeDeviationDegrees", "Gaze deviation from fixation (°)"),
            angle: -90,
            position: "insideLeft",
            offset: -10,
            style: { fill: "black", fontFamily: "inherit", fontSize: 12, textAnchor: "middle" },
          },
        }}
        xAxisProps={{
          type: "number",
          domain: [0, xDomainMaximum],
          ticks: xTicks,
          interval: 0,
          minTickGap: 0,
          tickFormatter: (value) => formatTickAtInterval(value, 30),
          axisLine: { stroke: "black" },
          tickLine: { stroke: "black" },
        }}
        xAxisLabel={t("timeSeconds", "Time (s)")}
        composedChartProps={{ margin: { top: 5, right: 55, bottom: 30, left: 25 } }}
        styles={{ axisLabel: { fill: "black" } }}
      >
        {blinkPoints.length > 0 && (
          <Scatter
            data={blinkPoints}
            dataKey="blink"
            yAxisId="left"
            shape="diamond"
            fill="black"
            line={false}
            isAnimationActive={false}
          />
        )}
      </CompositeChart>
    </Flex>
  );
}
